// url-utils.js
// Utility functions for parsing and building search URLs for city and tags

import { TAG_SEARCH_SETTINGS, LOGGING, EXPLORE_TAG_SETTINGS } from '/settings.js';

// Helper function for blog logging
function logBlog(message, data = null) {
    if (LOGGING.ENABLED && LOGGING.BLOG) {
        if (data) {
            console.log(`[BLOG] ${message}`, data);
        } else {
            console.log(`[BLOG] ${message}`);
        }
    }
}

/**
 * Normalizes a slug: lowercase, removes diacritics, replaces non-alphanum with underscores.
 * @param {string} str
 * @returns {string}
 */
export function normalizeSlug(str) {
    return typeof str === 'string'
        ? str
            .toLowerCase()
            .normalize('NFD').replace(/\p{Diacritic}/gu, '')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
        : '';
}

/**
 * parseUrlForIds: Handles URLs like ?id=1,2,3 OR /area/favorites/1,2,3
 * Returns an array of IDs.
 */
export function parseUrlForIds(url) {
    // 1. Legacy check: ?id=... (query string format)
    const search = url ? (new URL(url, window.location.origin)).search : window.location.search;
    const urlParams = new URLSearchParams(search);
    const legacyIds = urlParams.get('id');
    if (legacyIds) {
        const ids = legacyIds.split(',').map(id => id.trim()).filter(Boolean);
        if (typeof window !== 'undefined') {
            window.__openSingleSpotFromUrl = ids.length === 1;
        }
        return ids;
    }

    // 2. New format check: /area/favorites/1,2,3 (pathname format)
    const { mode, params } = parseUrlParams(url);
    if (mode === 'favorites' || mode === 'myfavorites') {
        const idsStr = params[0];
        if (idsStr) {
            // Decode in case of encoding
            const decoded = decodeURIComponent(idsStr);
            const ids = decoded.split(',').map(id => id.trim()).filter(Boolean);
            if (typeof window !== 'undefined') {
                window.__openSingleSpotFromUrl = ids.length === 1;
            }
            return ids;
        }
    }

    if (typeof window !== 'undefined') {
        window.__openSingleSpotFromUrl = false;
    }

    return [];
}

/**
 * Detects if the current URL is a blog post URL
 * Supports: /en/post-slug, /es/post-slug, /de/post-slug
 * @returns {Object|null} { language, postSlug } or null if not a blog post
 */
export function parseUrlForBlogPost() {
    const pathname = window.location.pathname;
    logBlog('parseUrlForBlogPost - pathname:', pathname);

    // Match pattern: /{language}/{post-slug}
    const blogPattern = /^\/(en|es|de)\/([^\/]+)$/;
    const match = pathname.match(blogPattern);

    logBlog('parseUrlForBlogPost - match:', match);

    if (!match) return null;

    const [, language, postSlug] = match;
    const result = { language, postSlug };
    logBlog('parseUrlForBlogPost - result:', result);
    return result;
}

// -----------------------------------------------------------------------------
// NEW AREA-FIRST PARSING LOGIC
// -----------------------------------------------------------------------------

/**
 * Parses the URL pathname with the new "Area First" logic.
 * Structure: /area/mode/param1/param2... or /area/pack/tags...
 * Example: /tenerife/explore/nature/beaches or /tenerife/nature/beaches
 * 
 * @param {string} [url] - Optional URL string to parse
 * @returns {Object} The parsed components
 *   - areaSlug: string | null (e.g., 'tenerife')
 *   - mode: string | null (e.g., 'explore', 'search', 'discover')
 *   - params: string[] (e.g., ['nature', 'beaches'])
 */
export function parseUrlParams(url) {
    // Get pathname from URL or current location
    let pathname;
    if (url) {
        try {
            const urlObj = new URL(url, window.location.origin);
            pathname = urlObj.pathname;
        } catch (e) {
            // If URL parsing fails, try to extract pathname manually
            pathname = url.startsWith('/') ? url.split('?')[0] : '/' + url.split('?')[0];
        }
    } else {
        pathname = window.location.pathname;
    }

    // Check if this is a blog post URL first (e.g., /en/post-slug)
    // Blog posts take precedence over area parsing
    const blogMatch = pathname.match(/^\/(en|es|de)\/([^\/]+)$/);
    if (blogMatch) {
        return { areaSlug: null, mode: null, params: [] };
    }

    // Remove leading / and split by /
    const parts = pathname.replace(/^\/+/, '').split('/').filter(Boolean);

    // Special Case: Direct ID links in query string (legacy support)
    // Check query string for ?id=123 format
    const search = url ? (new URL(url, window.location.origin)).search : window.location.search;
    if (search.includes('id=')) {
        // Even if ?id= is present, keep the area slug from the path if available
        // but do not infer a mode/params (IDs are handled separately).
        return { areaSlug: parts[0] || null, mode: null, params: [] };
    }

    // Special Case: Legacy query string formats (for backward compatibility)
    // Check if query string has legacy formats like ?map=slug or ?discover=slug
    if (search) {
        const urlParams = new URLSearchParams(search);
        if (urlParams.has('map') || urlParams.has('discover')) {
            const mapSlug = urlParams.get('map') || urlParams.get('discover');
            return { areaSlug: null, mode: 'map', params: [mapSlug] };
        }
    }

    if (parts.length === 0) {
        return { areaSlug: null, mode: null, params: [] };
    }

    // 1. First parameter is ALWAYS the Area
    const areaSlug = parts[0];

    // 2. Second parameter can be either a Mode, a Pack slug (explore), or a Map slug
    // Known modes: 'explore', 'search', 'favorites', 'map', 'discover'
    // NOTE: Pack detection is now dynamic (area-aware). We no longer rely on a hardcoded
    // EXPLORE_PACK_SLUGS list here; at startup the router will disambiguate ambiguous
    // second segments (i.e., when it's not a known mode) by checking the area's packs.
    const knownModes = ['explore', 'search', 'favorites', 'map', 'discover']; // 'discover' is legacy
    let mode = null;
    let params = [];

    if (parts.length > 1) {
        const secondPart = parts[1];
        // Check if it's a known mode
        if (knownModes.includes(secondPart)) {
            const rawMode = secondPart;
            // Map legacy 'discover' to 'map'
            mode = rawMode === 'discover' ? 'map' : rawMode;
            params = parts.slice(2); // everything after mode
        } else {
            // Not a known mode - treat as implicit map mode for now.
            // The runtime router may disambiguate this candidate as an Explore pack
            // by checking area packs at startup (area-aware detection).
            mode = 'map';
            params = parts.slice(1); // everything after area (including the map slug or pack candidate)
        }
    }

    return { areaSlug, mode, params };
}

/**
 * Builds a URL path for the new Area-First structure.
 * @param {string} areaSlug 
 * @param {string} mode 
 * @param {string[]} params 
 * @returns {string}
 */
export function buildAreaUrl(areaSlug, mode, params = []) {
    if (!areaSlug) return '/';

    // Base path: /area/mode
    let parts = [
        encodeURIComponent(areaSlug),
        mode ? encodeURIComponent(mode) : null
    ].filter(Boolean);

    let url = '/' + parts.join('/');

    if (params.length > 0) {
        // All params are separated by slashes
        // Replace encoded commas back to literal commas for readability (for favorites IDs)
        const encodedParams = params.map(p => encodeURIComponent(p).replace(/%2C/g, ','));
        url += '/' + encodedParams.join('/');
    }

    return url;
}

/**
 * Updates the browser URL using history.replaceState.
 */
export function updateUrl(areaSlug, mode, params = []) {
    const url = buildAreaUrl(areaSlug, mode, params);
    window.history.replaceState({}, '', url);
}

// -----------------------------------------------------------------------------
// LEGACY / COMPATIBILITY FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Updates the browser URL to reflect the current Search (city, tags).
 * Uses the new Area-First format: ?city&search&tags...
 * @param {string|Object} city - City slug or object
 * @param {string[]|Object[]} tags - Array of tag slugs or objects
 */
export function updateUrlForSearch(city, tags = []) {
    const citySlug = city?.slug || city;
    // Deduplicate tags and extract slugs
    const uniqueTags = [...new Set(tags.map(t => t?.slug || t))];

    // Use new generic updater
    updateUrl(citySlug, 'search', uniqueTags);
}

/**
 * Updates the browser URL to reflect the current Explore (area, pack, tags).
 * Uses simplified format: /area/pack/tag1&tag2&tag3 (no 'explore' mode needed, tags separated by &)
 * @param {string|Object} area - Area slug or object
 * @param {string|Object} pack - Pack slug or object
 * @param {string[]|Object[]} tags - Array of tag slugs or objects
 */
export function updateUrlForExplore(area, pack, tags = []) {
    const areaSlug = area?.slug || area;
    const packSlug = pack?.slug || pack;

    // Convert 'default' to 'all' for URL display
    let urlTags = tags.map(t => {
        const slug = t?.slug || t;
        return slug === 'default' ? 'all' : slug;
    });
    // Deduplicate tags
    urlTags = [...new Set(urlTags)];

    // Use simplified format: /area/pack/tag1&tag2&tag3
    // Pack gets a slash, then tags are joined with & (not slashes)
    if (packSlug) {
        const tagsStr = urlTags.length > 0 ? urlTags.join('&') : '';
        const url = tagsStr ? `/${encodeURIComponent(areaSlug)}/${encodeURIComponent(packSlug)}/${tagsStr}` : `/${encodeURIComponent(areaSlug)}/${encodeURIComponent(packSlug)}`;
        window.history.replaceState({}, '', url);
    } else {
        // No pack, just area and tags
        const tagsStr = urlTags.length > 0 ? urlTags.join('&') : '';
        const url = tagsStr ? `/${encodeURIComponent(areaSlug)}/${tagsStr}` : `/${encodeURIComponent(areaSlug)}`;
        window.history.replaceState({}, '', url);
    }
}

/**
 * Updates the browser URL to reflect the current Discover map selection.
 * Uses new format /area/mapSlug if we have an area,
 * or legacy format ?map=slug for global maps if area is unknown.
 * 
 * @param {Object} mapOrCapsule - Map or capsule object with a .slug property
 */
export function updateUrlForDiscover(mapOrCapsule) {
    if (!mapOrCapsule || !mapOrCapsule.slug) return;

    // Check if we currently have an area selected in the URL
    const { areaSlug } = parseUrlParams();

    if (areaSlug) {
        // Use simplified format: /area/mapSlug (no 'map' mode needed)
        // This creates URLs like /tenerife/best_viewpoints
        const url = buildAreaUrl(areaSlug, null, [mapOrCapsule.slug]);
        window.history.replaceState({}, '', url);
    } else {
        // Fallback to legacy global format: ?map=mapSlug (query string for backward compatibility)
        const url = `?map=${encodeURIComponent(mapOrCapsule.slug)}`;
        window.history.replaceState({}, '', url);
    }
}

/**
 * Legacy helper: parseUrlForExploreAreaPackTag
 * Still used by legacy code, but we should aim to replace usage with parseUrlParams
 */
export async function parseUrlForExploreAreaPackTag(url) {
    // Shared helper to limit tags array to configured maximum
    const limitTags = (tagsArray) => {
        const maxTags = EXPLORE_TAG_SETTINGS.MAX_TAGS_PER_EXPLORE;
        return tagsArray.slice(0, maxTags);
    };

    // Try new parser first
    let { areaSlug, mode, params } = parseUrlParams(url);

    // If mode is 'map' but we have an area and params, it might be an implicit explore pattern
    // Use disambiguation to check if second segment is actually a pack
    if (areaSlug && mode === 'map' && params.length > 0) {
        const result = await disambiguateAreaSecondSegment(areaSlug, params);
        if (result.mode === 'explore') {
            mode = 'explore';
            params = result.params;
        }
    }

    // If it matches the new "explore" pattern (explicit or implicit)
    if (areaSlug && mode === 'explore') {
        // structure: area, pack, tags (simplified) or area, explore, pack, tags (explicit)
        // Tags are separated by &, not /
        // If mode is 'explore', params[0] is the pack slug
        const pack = params[0] || null;
        // Tags are in params[1] as a single string with & separators (if present)
        let tags = [];
        if (params.length > 1 && params[1]) {
            // Split by & to get individual tags, limit to configured maximum
            tags = limitTags(params[1].split('&').filter(Boolean));
        }
        if (tags.length === 0) tags = ['all'];
        return { area: areaSlug, pack, tags };
    }

    // Fallback to old parser for backward compatibility (legacy query string format)
    const search = url ? (new URL(url, window.location.origin)).search : window.location.search;
    if (search) {
        const parts = search.replace(/^\?/, '').split('&').filter(Boolean);
        if (parts.length > 0 && parts[0].startsWith('explore=')) {
            const area = parts[0].split('=')[1];
            const pack = parts[1] || null;
            // Limit tags to configured maximum
            let tags = limitTags(parts.slice(2));
            if (!pack) return null;
            if (tags.length === 0) tags = ['all'];
            return { area, pack, tags };
        }
    }
    return null;
}

export function parseUrlForSearchBarCityTags(url) {
    // Try new parser (pathname format)
    const { areaSlug, mode, params } = parseUrlParams(url);
    if (areaSlug && mode === 'search') {
        return { city: areaSlug, tags: params };
    }

    // Fallback to legacy query string format
    const search = url ? (new URL(url, window.location.origin)).search : window.location.search;
    if (search) {
        const parts = search.replace(/^\?/, '').split('&').filter(Boolean);
        if (parts.length > 0 && parts[0].startsWith('search=')) {
            const city = parts[0].split('=')[1];
            const tags = parts.slice(1);
            return { city, tags };
        }
    }
    return { city: null, tags: [] };
}

export function parseUrlForDiscover(url) {
    const { areaSlug, mode, params } = parseUrlParams(url);
    // Handle explicit map mode or implicit map mode (when mode is null but we have params and area)
    if (mode === 'map' || mode === 'discover' || (mode === null && areaSlug && params.length > 0)) {
        // In new format: /area/mapSlug (simplified, no 'map' mode) or /area/map/mapSlug (explicit)
        // Or legacy /area/discover/mapSlug
        return { mapSlug: params[0] || null, capsuleSlug: params[0] || null }; // Keep capsuleSlug for backward compatibility
    }

    // Fallback - check legacy query string formats
    const search = url ? (new URL(url, window.location.origin)).search : window.location.search;
    if (search) {
        const urlParams = new URLSearchParams(search);
        const mapSlug = urlParams.get('map') || urlParams.get('discover'); // Support both legacy formats
        if (mapSlug) {
            return { mapSlug: mapSlug, capsuleSlug: mapSlug }; // Keep capsuleSlug for backward compatibility
        }
    }
    return { mapSlug: null, capsuleSlug: null }; // Keep capsuleSlug for backward compatibility
}

/**
 * Disambiguate a second path segment for an area: decides if it's an Explore pack or a Map slug.
 * @param {string} areaSlug
 * @param {Array<string>} paramsParts - the params array from parseUrlParams (parts after area)
 * @returns {Promise<{mode: string, params: string[]}>}
 */
export async function disambiguateAreaSecondSegment(areaSlug, paramsParts) {
    if (!areaSlug || !paramsParts || paramsParts.length === 0) {
        return { mode: 'map', params: paramsParts || [] };
    }
    try {
        const candidate = paramsParts[0];
        if (!candidate) return { mode: 'map', params: paramsParts };
        // Lazy-import helpers to avoid circular deps
        const { getPacksForArea } = await import('./spot-queries.js');
        const { getAreaBySlug } = await import('../area-selector/index.js');
        const area = await getAreaBySlug(areaSlug);
        if (!area) return { mode: 'map', params: paramsParts };
        const packs = await getPacksForArea(area.id);
        const normalizedCandidate = normalizeSlug(candidate);
        const matched = packs.find(p => normalizeSlug(p.slug) === normalizedCandidate);
        if (matched) {
            // Rebuild explore-style params: [packSlug, tagsString?]
            let tagsStr = null;
            if (paramsParts.length > 1) {
                // Join remaining segments with '&' to preserve both /pack/tag1/tag2 and /pack/tag1&tag2
                tagsStr = paramsParts.slice(1).join('&');
            }
            return { mode: 'explore', params: tagsStr ? [matched.slug, tagsStr] : [matched.slug] };
        }
    } catch (e) {
        if (LOGGING.ENABLED && LOGGING.CACHE) console.warn('[url-utils] disambiguateAreaSecondSegment error:', e);
    }
    return { mode: 'map', params: paramsParts };
}

export function normalizeExploreTagsForBackend(tags) {
    return tags.map(tag => tag === 'all' ? 'default' : tag);
}

// --- Re-export existing helpers ---
export function buildUrlForIds(ids) {
    if (!Array.isArray(ids) || ids.length === 0) {
        // If clearing IDs, try to preserve Area context
        const { areaSlug } = parseUrlParams();
        if (areaSlug) return buildAreaUrl(areaSlug, null);
        return window.location.origin + '/';
    }

    // Check if we are in an Area context
    const { areaSlug } = parseUrlParams();
    if (areaSlug) {
        // Use new Area-First format: /area/favorites/1,2,3
        // We treat the comma-separated list as a single parameter
        return buildAreaUrl(areaSlug, 'favorites', [ids.join(',')]);
    }

    // Fallback to legacy global format (query string for backward compatibility)
    const queryString = 'id=' + ids.join(',');
    return `${window.location.origin}${window.location.pathname}?${queryString}`;
}

export function updateUrlWithIds(ids) {
    const newUrl = buildUrlForIds(ids);
    window.history.replaceState({}, '', newUrl);
}

export function clearUrl() {
    window.history.replaceState({}, '', window.location.pathname);
}

export function showUserLocation(latLng) {
    return L.marker(latLng, {
        icon: L.divIcon({
            className: 'show-user-location',
            html: '<i class="fa-solid fa-location-crosshairs"></i>',
            iconSize: [28, 28],
            iconAnchor: [14, 14],
            popupAnchor: [0, -14]
        })
    });
}

export function parseUrlForFavorites(url) {
    return null;
}
